import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CandidateLanguage, JobLanguage, CEFRLevel } from "@/types/candidate";
import { toast } from "@/hooks/use-toast";

export const useCandidateLanguages = (candidateId?: string) => {
  const [languages, setLanguages] = useState<CandidateLanguage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLanguages = async () => {
    if (!candidateId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('candidate_languages')
        .select('*')
        .eq('candidate_id', candidateId)
        .order('created_at');

      if (error) throw error;
      setLanguages(data || []);
    } catch (error) {
      console.error('Error fetching candidate languages:', error);
      toast({
        title: "Error",
        description: "Failed to load candidate languages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateLanguages = async (updatedLanguages: CandidateLanguage[]) => {
    if (!candidateId) return;

    try {
      // Delete existing languages that are not in the updated list
      const existingIds = languages.map(l => l.id).filter(Boolean);
      const updatedIds = updatedLanguages.map(l => l.id).filter(Boolean);

      const toDelete = existingIds.filter(id => !updatedIds.includes(id));
      if (toDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('candidate_languages')
          .delete()
          .eq('candidate_id', candidateId)
          .in('id', toDelete);

        if (deleteError) throw deleteError;
      }

      // Update or insert languages
      // Each language touches its own row, so the writes can run together
      await Promise.all(updatedLanguages.map(async (lang) => {
        if (lang.id) {
          // Update existing
          const { error } = await supabase
            .from('candidate_languages')
            .update({
              language: lang.language,
              language_code: lang.language_code,
              cefr_level: lang.cefr_level,
              is_user_owned: lang.is_user_owned
            })
            .eq('id', lang.id);

          if (error) throw error;
        } else {
          // Insert new
          const { error } = await supabase
            .from('candidate_languages')
            .insert({
              candidate_id: candidateId,
              language: lang.language,
              language_code: lang.language_code,
              cefr_level: lang.cefr_level,
              is_user_owned: lang.is_user_owned
            });

          if (error) throw error;
        }
      }));

      await fetchLanguages(); // Refresh the list
      toast({
        title: "Success",
        description: "Candidate languages updated successfully",
      });
    } catch (error) {
      console.error('Error updating candidate languages:', error);
      toast({
        title: "Error",
        description: "Failed to update candidate languages",
        variant: "destructive",
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchLanguages();
  }, [candidateId]);

  return {
    languages,
    loading,
    updateLanguages,
    refetch: fetchLanguages
  };
};